import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosError,
} from "axios";
import { getAccessToken } from "./tokenManager";
import { cache } from "./apiCache";

/** --- THROTTLE / QUEUE KATMANI BAŞLANGIÇ --- */
declare module "axios" {
  interface InternalAxiosRequestConfig {
    /** Bu isteği kuyruğu atlayarak hemen gönder (ör: /auth/refresh) */
    skipQueue?: boolean;
  }
}

type ReleaseFn = () => void;

type QueueOptions = {
  maxConcurrent: number; // aynı anda kaç istek
  burstDelayMs: number; // her tamamlanan isteğin ardından ne kadar bekle
};

class RequestQueue {
  private maxConcurrent: number;
  private burstDelayMs: number;
  private active = 0;
  private pending: Array<(release: ReleaseFn) => void> = [];
  private locked = false;

  constructor(opts: QueueOptions) {
    this.maxConcurrent = opts.maxConcurrent;
    this.burstDelayMs = opts.burstDelayMs;
  }

  configure(opts: Partial<QueueOptions>) {
    if (opts.maxConcurrent !== undefined)
      this.maxConcurrent = opts.maxConcurrent;
    if (opts.burstDelayMs !== undefined) this.burstDelayMs = opts.burstDelayMs;
  }

  async acquire(): Promise<ReleaseFn> {
    return new Promise<ReleaseFn>((resolve) => {
      const tryDequeue = () => {
        if (!this.locked && this.active < this.maxConcurrent) {
          this.active++;
          let released = false;
          const release: ReleaseFn = () => {
            if (released) return;
            released = true;
            this.active--;

            // kısa gecikme — tek tek gitsin istiyorsan küçük bir ms değeri tut
            this.locked = true;
            setTimeout(() => {
              this.locked = false;
              // sıradakileri uyandır
              const next = this.pending.shift();
              if (next) next(tryDequeue as unknown as ReleaseFn);
            }, this.burstDelayMs);

            // sıradaki iş varsa hemen tetikle
            const next = this.pending.shift();
            if (next) next(tryDequeue as unknown as ReleaseFn);
          };

          resolve(release);
        } else {
          this.pending.push(() =>
            resolve(this.acquire() as unknown as ReleaseFn)
          );
        }
      };

      tryDequeue();
    });
  }
}

// Varsayılan: TEK TEK (1) + 60ms ara
const globalQueue = new RequestQueue({ maxConcurrent: 2, burstDelayMs: 60 });

export function configureGlobalThrottle(opts: Partial<QueueOptions>) {
  globalQueue.configure(opts);
}

function attachThrottling(instance: AxiosInstance) {
  // Kuyruğa giriş — bunu İLK request interceptor olarak ekliyoruz
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (config.skipQueue) return config;

      const release = await globalQueue.acquire();
      Object.defineProperty(config, "__queueRelease", {
        value: release,
        writable: true,
        enumerable: false,
      });

      return config;
    }
  );

  const doRelease = (config?: InternalAxiosRequestConfig) => {
    const rel = (config as any)?.__queueRelease as ReleaseFn | undefined;
    if (typeof rel === "function") {
      try {
        rel();
      } finally {
        (config as any).__queueRelease = undefined;
      }
    }
  };

  // Release’i hem success hem error’larda garanti altına al
  instance.interceptors.response.use(
    (res) => {
      doRelease(res.config);
      return res;
    },
    (err: AxiosError) => {
      if (err.config) doRelease(err.config);
      return Promise.reject(err);
    }
  );
}
/** --- THROTTLE / QUEUE KATMANI BİTİŞ --- */

const API_ENDPOINTS = {
  AUTH: process.env.REACT_APP_AUTH_API_URL || "https://auth.saloncepte.com",
  APPOINTMENT:
    process.env.REACT_APP_APPOINTMENT_API_URL ||
    "https://appointment.saloncepte.com",
  CATALOG:
    process.env.REACT_APP_CATALOG_API_URL || "https://catalog.saloncepte.com",
  CONTENT:
    process.env.REACT_APP_CONTENT_API_URL || "https://content.saloncepte.com",
  EMPLOYEE:
    process.env.REACT_APP_EMPLOYEE_API_URL || "https://employee.saloncepte.com",
  FILE: process.env.REACT_APP_FILE_API_URL || "https://file.saloncepte.com",
  SERVICE:
    process.env.REACT_APP_SERVICE_API_URL || "https://service.saloncepte.com",
  FINANCE:
    process.env.REACT_APP_FINANCE_API_URL || "https://finance.saloncepte.com",
  INVENTORY:
    process.env.REACT_APP_INVENTORY_API_URL ||
    "https://inventory.saloncepte.com",
  PAYMENT:
    process.env.REACT_APP_PAYMENT_API_URL || "https://payment.saloncepte.com",
  SALON: process.env.REACT_APP_SALON_API_URL || "https://salon.saloncepte.com",
  SUBSCRIPTION:
    process.env.REACT_APP_SUBSCRIPTION_API_URL ||
    "https://subscription.saloncepte.com",
  NOTIFICATION:
    process.env.REACT_APP_NOTIFICATION_API_URL ||
    "https://notification.saloncepte.com",
};

const REQUEST_TIMEOUT = 30000;

interface ApiInstance extends AxiosInstance {
  post<T = any>(url: string, data?: any, config?: any): Promise<T>;
  get<T = any>(url: string, config?: any): Promise<T>;
  put<T = any>(url: string, data?: any, config?: any): Promise<T>;
  delete<T = any>(url: string, config?: any): Promise<T>;
  patch<T = any>(url: string, data?: any, config?: any): Promise<T>;
}

const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers["X-User-Token"] = token;
  }
  return config;
};

const responseInterceptor = (response: AxiosResponse) => response.data;

const errorInterceptor = async (error: any) => {
  if (error.response?.status === 401) {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    window.location.href = "/giris-yap";
  }

  console.error("API Error:", error);
  return Promise.reject(error);
};

const createApiInstance = (baseURL: string): ApiInstance => {
  const instance = axios.create({
    baseURL,
    timeout: REQUEST_TIMEOUT,
    headers: {
      "Content-Type": "application/json",
      accept: "text/plain",
    },
  }) as ApiInstance;

  // 1) Kuyruk interseptörlerini önce ekle (giriş/çıkış)
  attachThrottling(instance);

  // 2) Sonra senin mevcut interceptor’ların
  instance.interceptors.request.use(requestInterceptor, Promise.reject);
  instance.interceptors.response.use(responseInterceptor, errorInterceptor);

  return instance;
};

const apiService: Record<keyof typeof API_ENDPOINTS, ApiInstance> = {} as any;

Object.entries(API_ENDPOINTS).forEach(([key, url]) => {
  apiService[key as keyof typeof API_ENDPOINTS] = createApiInstance(url);
});

export const apiGet = <T>(
  service: keyof typeof apiService,
  url: string,
  config?: any
) => apiService[service].get<T>(url, config);

export const apiPost = <T>(
  service: keyof typeof apiService,
  url: string,
  data?: any,
  config?: any
) => apiService[service].post<T>(url, data, config);

export const apiPut = <T>(
  service: keyof typeof apiService,
  url: string,
  data?: any,
  config?: any
) => apiService[service].put<T>(url, data, config);

export const apiDelete = <T>(
  service: keyof typeof apiService,
  url: string,
  config?: any
) => apiService[service].delete<T>(url, config);

export const apiPatch = <T>(
  service: keyof typeof apiService,
  url: string,
  data?: any,
  config?: any
) => apiService[service].patch<T>(url, data, config);

type CacheStore = "memory" | "localStorage";

type ApiCachedGetOpts = {
  ttl?: number; // ms
  store?: CacheStore; // default memory
  swr?: boolean; // stale-while-revalidate
  keyExtra?: string; // cache key’e ekstra
  config?: any; // axios get config
};

export function apiCachedGet<T>(
  service: keyof typeof apiService,
  url: string,
  opts: ApiCachedGetOpts = {}
) {
  const key = cache.makeKey(String(service), url, opts.keyExtra);
  return cache.cachedGet<T>(
    String(service),
    url,
    () => apiService[service].get<T>(url, opts.config),
    {
      ttl: opts.ttl ?? 5 * 60 * 1000,
      store: opts.store ?? "memory",
      staleWhileRevalidate: opts.swr ?? false,
      key,
    }
  );
}

export const invalidateCacheKey = (key: string) => cache.invalidate(key);
export const invalidateCachePrefix = (prefix: string) =>
  cache.invalidatePrefix(prefix);
export const clearAllApiCache = () => cache.clearAll();

export default apiService;
